import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, MessageCircle, Phone, Gift, Clock } from 'lucide-react';

export const LeadForm = ({ propertyId = null, propertyTitle = '', leadType = 'inquiry', title = '', showBadges = true }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    budget: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only include email if it's not empty
      const payload = {
        name: formData.name,
        phone: formData.phone,
        message: formData.message,
        property_id: propertyId,
        lead_type: leadType,
      };
      
      if (formData.email && formData.email.trim()) {
        payload.email = formData.email;
      }
      if (formData.budget && formData.budget.trim()) {
        payload.budget = formData.budget;
      }
      
      await api.post('/leads', payload);
      
      toast.success('🎉 Success! Our team will call you within 10 minutes!');
      setFormData({
        name: '',
        phone: '',
        email: '',
        budget: '',
        message: '',
      });
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to submit. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const whatsappNumber = '917020970161';
  const whatsappMessage = propertyTitle
    ? `Hi, I'm interested in ${propertyTitle}`
    : 'Hi, I\'m interested in your properties';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect border-2 border-rose-gold/30 rounded-2xl p-8 shadow-rose-glow relative overflow-hidden"
    >
      {/* Urgency Badges */}
      {showBadges && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-500 to-orange-500 text-black py-2 px-4 text-center font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 rounded-t-2xl">
          <Clock size={14} />
          <span>⚡ Limited Time Offer - Best Prices Guaranteed!</span>
        </div>
      )}

      <div className={showBadges ? 'mt-8' : ''}>
        <h3 className="font-heading text-3xl font-semibold text-white mb-2">
          {title || (propertyTitle ? 'Get Best Price Now!' : 'Get FREE Consultation')}
        </h3>
        <p className="text-white/60 text-sm mb-6 uppercase tracking-widest">
          We'll call you within 10 minutes
        </p>

        {showBadges && (
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="inline-flex items-center gap-1 bg-green-500/20 border border-green-500/50 text-green-400 px-3 py-1 rounded-full text-xs font-semibold">
              <Gift size={12} />
              Free Site Visit
            </span>
            <span className="inline-flex items-center gap-1 bg-rose-gold/20 border border-rose-gold/50 text-rose-gold px-3 py-1 rounded-full text-xs font-semibold">
              Best Price Guaranteed
            </span>
            <span className="inline-flex items-center gap-1 bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-1 rounded-full text-xs font-semibold">
              Expert Guidance
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
          <div>
            <Label htmlFor="name" className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Full Name *
            </Label>
            <Input
              id="name"
              name="name"
              data-testid="lead-form-name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-dark border border-dark-lighter focus:border-rose-gold text-white rounded-lg px-4 py-3 transition-colors"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Phone Number *
            </Label>
            <Input
              id="phone"
              name="phone"
              data-testid="lead-form-phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full bg-dark border border-dark-lighter focus:border-rose-gold text-white rounded-lg px-4 py-3 transition-colors"
              placeholder="+91 XXXXX XXXXX"
            />
          </div>

          <div>
            <Label htmlFor="budget" className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Budget Range
            </Label>
            <Input
              id="budget"
              name="budget"
              data-testid="lead-form-budget"
              value={formData.budget}
              onChange={handleChange}
              className="w-full bg-dark border border-dark-lighter focus:border-rose-gold text-white rounded-lg px-4 py-3 transition-colors"
              placeholder="e.g., 50L - 1Cr"
            />
          </div>

          <div>
            <Label htmlFor="message" className="text-sm font-semibold text-rose-gold mb-2 block uppercase tracking-wider">
              Message *
            </Label>
            <Textarea
              id="message"
              name="message"
              data-testid="lead-form-message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={3}
              className="w-full bg-dark border border-dark-lighter focus:border-rose-gold text-white rounded-lg px-4 py-3 transition-colors resize-none"
              placeholder="Tell us your requirements..."
            />
          </div>

          <Button
            type="submit"
            data-testid="lead-form-submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wider py-6 text-lg font-bold animate-pulse"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Phone size={20} />
                Get FREE Callback Now!
              </>
            )}
          </Button>

          <a
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-button"
            className="block w-full text-center bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full font-bold transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider shadow-lg"
          >
            <MessageCircle size={20} />
            Chat on WhatsApp - Instant Reply!
          </a>
        </form>

        <p className="text-white/40 text-xs text-center mt-4">
          🔒 Your information is 100% secure and confidential
        </p>
      </div>
    </motion.div>
  );
};

export default LeadForm;
